import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { formatDate, formatToZMW } from "../../../helpers";
import { FaSpinner } from "react-icons/fa";
import { calculateFinancialPosition, getCoaName, CONTROL_ACCOUNTS } from "../../../utils/reportCalculations";

const BalanceSheet = ({ rdata, startDate, endDate }) => {
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const { allData } = useOutletContext();

  useEffect(() => {
    if (!rdata?.bills) return;

    setPosition(calculateFinancialPosition(rdata, allData));
    setLoading(false);
  }, [rdata, allData]);

  return (
    <div className="w-full min-h-[500px] h-full rounded-md p-3">
      {loading ? (
        <div className="flex justify-center items-center h-full w-full text-blue-950 text-5xl p-5 m-5">
          <FaSpinner size={50} className="spinner" />
        </div>
      ) : (
        <div className="container mx-auto p-4">
          <div className="text-center">
            <h2 className="font-bold text-2xl">{allData.council[0]?.name.toUpperCase()}</h2>
            <h2 className="font-bold text-2xl my-3">FINANCIAL POSITION</h2>
            <h3 className="text-xl font-bold">As at {formatDate(endDate)}</h3>
          </div>

          <table id="balance-sheet" className="w-full g-table mt-4">
            <tbody>
              <tr className="bg-blue-950 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Assets</td>
                <td className="px-4 py-3 border-b border-gray-300">Amount</td>
              </tr>

              {position.assetLines.map((line, index) => (
                <tr key={`asset-${index}`}>
                  <td className="px-4 py-3 border-b border-gray-300">{line.name}</td>
                  <td className="px-4 py-3 border-b border-gray-300">{formatToZMW(line.amount)}</td>
                </tr>
              ))}

              <tr className="bg-gray-800 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Total Assets</td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.totalAssets)}
                </td>
              </tr>

              <tr className="bg-blue-950 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Liabilities</td>
                <td className="px-4 py-3 border-b border-gray-300">Amount</td>
              </tr>

              <tr>
                <td className="px-4 py-3 border-b border-gray-300">
                  {getCoaName(CONTROL_ACCOUNTS.CREDITORS, allData)}
                </td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.creditors)}
                </td>
              </tr>

              <tr className="bg-gray-800 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Total Liabilities</td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.creditors)}
                </td>
              </tr>

              <tr className="bg-blue-950 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Equity</td>
                <td className="px-4 py-3 border-b border-gray-300">Amount</td>
              </tr>

              <tr>
                <td className="px-4 py-3 border-b border-gray-300">
                  {getCoaName(CONTROL_ACCOUNTS.ACCUMULATED_FUND, allData)}
                </td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.accumulatedFund)}
                </td>
              </tr>

              <tr>
                <td className="px-4 py-3 border-b border-gray-300">Surplus (Deficit) brought forward</td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.openingSurplus || 0)}
                </td>
              </tr>

              <tr>
                <td className="px-4 py-3 border-b border-gray-300">Surplus (Deficit) for period</td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.surplus)}
                </td>
              </tr>

              <tr className="bg-gray-800 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Total Equity</td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.totalEquity)}
                </td>
              </tr>

              <tr className="bg-gray-800 font-bold text-white">
                <td className="px-4 py-3 border-b border-gray-300">Liabilities plus Equity</td>
                <td className="px-4 py-3 border-b border-gray-300">
                  {formatToZMW(position.totalLiabilitiesEquity)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className={`mt-4 ${position.isBalanced ? "text-blue-500" : "text-red-500"}`}>
            Financial Position is {position.isBalanced ? "Balanced" : "Not Balanced"}
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceSheet;
